import { computed, ref, watch } from 'vue';
import {
  READING_FONTS,
  READING_LINES,
  READING_WIDTHS,
  type ReadingFont,
  type ReadingLineHeight,
  type ReadingWidth,
} from '@s-blog/shared-types';

/**
 * 文章阅读偏好 (模块级单例)。两层模型: localStorage 覆盖 > 站长默认 (applyOwnerDefault) > 内置默认。
 * - 字号 / 行距: 写 documentElement CSS 变量 (--reading-fs / --reading-lh), .aurora-prose 消费 (代码块独立)
 * - 宽度: Post 正文栏 760→880px (Container + TOC 定位跟随, 由 Post.vue 消费)
 * **持久化只在显式 setter 时写** —— CSS 变量副作用每次都跑, 但落盘只在访客主动改时, 保住站长默认能生效。
 */
export type { ReadingFont, ReadingLineHeight, ReadingWidth };

const FONT_PX: Record<ReadingFont, number> = { small: 15.5, normal: 17, large: 19 };
const LH: Record<ReadingLineHeight, number> = { compact: 1.65, normal: 1.8, loose: 2.0 };
const WIDTH_PX: Record<ReadingWidth, number> = { normal: 760, wide: 880 };

const FONT_KEY = 'aurora-reading-font';
const LINE_KEY = 'aurora-reading-line';
const WIDTH_KEY = 'aurora-reading-width';

function read<T extends string>(key: string, valid: readonly T[]): T | null {
  try {
    const s = localStorage.getItem(key);
    if (s && (valid as readonly string[]).includes(s)) return s as T;
  } catch {
    // SSR / privacy mode
  }
  return null;
}
function write(key: string, v: string) {
  try {
    localStorage.setItem(key, v);
  } catch {
    // ignore
  }
}

const storedFont = read(FONT_KEY, READING_FONTS);
const storedLine = read(LINE_KEY, READING_LINES);
const storedWidth = read(WIDTH_KEY, READING_WIDTHS);

const font = ref<ReadingFont>(storedFont ?? 'normal');
const lineHeight = ref<ReadingLineHeight>(storedLine ?? 'normal');
const width = ref<ReadingWidth>(storedWidth ?? 'normal');

// CSS 变量副作用 (每次都跑, 不落盘)
watch(
  [font, lineHeight],
  () => {
    const root = document.documentElement.style;
    root.setProperty('--reading-fs', `${FONT_PX[font.value]}px`);
    root.setProperty('--reading-lh', String(LH[lineHeight.value]));
  },
  { immediate: true },
);

export function useReadingPrefs() {
  const widthPx = computed(() => WIDTH_PX[width.value]);
  const isWide = computed(() => width.value === 'wide');

  // 访客显式改 → 落盘 (从此覆盖站长默认)
  function setFont(v: ReadingFont) {
    font.value = v;
    write(FONT_KEY, v);
  }
  function setLineHeight(v: ReadingLineHeight) {
    lineHeight.value = v;
    write(LINE_KEY, v);
  }
  function setWidth(v: ReadingWidth) {
    width.value = v;
    write(WIDTH_KEY, v);
  }

  // 站长默认 (SiteConfig): 仅当对应项访客从没设过时套用, 不落盘
  function applyOwnerDefault(opts: {
    font?: ReadingFont | null;
    line?: ReadingLineHeight | null;
    width?: ReadingWidth | null;
  }) {
    if (!storedFont && opts.font && (READING_FONTS as readonly string[]).includes(opts.font)) {
      font.value = opts.font;
    }
    if (!storedLine && opts.line && (READING_LINES as readonly string[]).includes(opts.line)) {
      lineHeight.value = opts.line;
    }
    if (!storedWidth && opts.width && (READING_WIDTHS as readonly string[]).includes(opts.width)) {
      width.value = opts.width;
    }
  }

  return { font, lineHeight, width, widthPx, isWide, setFont, setLineHeight, setWidth, applyOwnerDefault };
}
